/**
 * Source Record Summary Components
 *
 * Read-only summary panels for Promise to Pay, Collection Case, and Dispute records
 * displayed within the Task Details Drawer
 */

import { format } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Flag,
  HandCoins,
  MessageSquare,
  Target,
  TrendingUp,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import { useCollectionCaseDetail } from '@/hooks/useCaseDetailData';
import { useDisputeByNumericId } from '@/hooks/useDisputesData';
import { usePromiseById } from '@/hooks/usePromisesToPayData';
import { cn, formatCurrency } from '@/lib/utils';
import { getTotalLevelsForDisputeType } from '@/lib/utils/dispute-workflow-utils';
import type { CollectionCase, PromiseToPay } from '@/types/collections';
import type { DisputeUI } from '@/types/ui/disputes';

// Promise Summary Panel
interface PromiseSummaryPanelProps {
  readonly promiseId: string;
}

export function PromiseSummaryPanel({ promiseId }: PromiseSummaryPanelProps) {
  const { selectedCompany } = useCompany();
  const { data: promise = null, isLoading: loading } = usePromiseById(promiseId);

  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants = {
      Active: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
      Broken: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
      Paid: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
      'Partially Paid':
        'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', variants[status])}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!promise) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Promise Not Found
          </CardTitle>
          <CardDescription>Unable to load promise details</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          Promise Summary
        </CardTitle>
        <CardDescription>Key details about this payment commitment</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Two-column grid layout */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {/* Promise Date */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Promise Date
            </Label>
            <p className="text-sm font-semibold">{format(promise.promiseDate, 'MMM dd, yyyy')}</p>
          </div>

          {/* Promised Amount */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CurrencyIcon currencyCode={promise.currencyCode || selectedCompany?.currency} className="h-3 w-3" />
              Promised Amount
            </Label>
            <p className="text-sm font-bold">
              {formatCurrency(promise.promisedAmount, promise.currencyCode || selectedCompany?.currency || 'GBP')}
            </p>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Status
            </Label>
            <div>{getStatusBadge(promise.status)}</div>
          </div>

          {/* Confirmation Method */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Confirmation Method
            </Label>
            <p className="text-sm">{promise.confirmationMethod}</p>
          </div>

          {/* Case Number */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Case Number
            </Label>
            <p className="text-sm font-medium">{promise.caseNumber}</p>
          </div>

          {/* Created By */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" />
              Created By
            </Label>
            <p className="text-sm">{promise.createdBy}</p>
          </div>
        </div>

        {/* Notes - Full Width */}
        {promise.notes && (
          <div className="space-y-1 pt-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Notes
            </Label>
            <p className="text-sm text-muted-foreground">{promise.notes}</p>
          </div>
        )}

        {/* View Source Button */}
        <Link to={`/collections/promises/${promise.id}`} className="block pt-2">
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            View & Edit Promise
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

// Case Summary Panel
interface CaseSummaryPanelProps {
  readonly caseId: string;
}

export function CaseSummaryPanel({ caseId }: CaseSummaryPanelProps) {
  const { selectedCompany } = useCompany();
  const { data: collectionCase = null, isLoading: loading } = useCollectionCaseDetail(caseId);

  const getStatusBadge = (status: CollectionCase['status']) => {
    const variants = {
      Active: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
      'On Hold':
        'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
      Closed: 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
      Promised:
        'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800',
      Escalated: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', variants[status])}>
        {status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: CollectionCase['priority']) => {
    const variants = {
      High: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
      Medium:
        'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
      Low: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', variants[priority])}>
        {priority}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!collectionCase) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Case Not Found
          </CardTitle>
          <CardDescription>Unable to load case details</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          Case Summary
        </CardTitle>
        <CardDescription>Key details about this collection case</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Two-column grid layout */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {/* Case ID */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Case ID
            </Label>
            <p className="text-sm font-semibold">{collectionCase.caseNumber || collectionCase.caseId}</p>
          </div>

          {/* Amount */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CurrencyIcon currencyCode={collectionCase.currency || selectedCompany?.currency} className="h-3 w-3" />
              Amount
            </Label>
            <p className="text-sm font-bold">
              {formatCurrency(
                collectionCase.displayOutstandingAmount ?? collectionCase.baseAmount ?? 0,
                collectionCase.currency || selectedCompany?.currency || 'GBP',
              )}
            </p>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Status
            </Label>
            <div>{getStatusBadge(collectionCase.status)}</div>
          </div>

          {/* Priority */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Flag className="h-3 w-3" />
              Priority
            </Label>
            <div>{getPriorityBadge(collectionCase.priority)}</div>
          </div>

          {/* Days in Collection */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Days in Collection
            </Label>
            <p className="text-sm font-medium">{collectionCase.daysInCollection} days</p>
          </div>

          {/* Strategy Phase */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              Strategy Phase
            </Label>
            <p className="text-sm">
              {collectionCase.strategyPhase.phaseName} ({collectionCase.strategyPhase.currentPhase}/
              {collectionCase.strategyPhase.totalPhases})
            </p>
          </div>

          {/* Next Action */}
          <div className="space-y-1 col-span-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Next Action
            </Label>
            <p className="text-sm">
              {collectionCase.nextAction}
              {collectionCase.nextAction === 'No further actions scheduled'
                ? ''
                : ` - ${format(collectionCase.nextActionDate, 'MMM dd, yyyy')}`}
            </p>
          </div>

          {/* Assigned To */}
          {collectionCase.assignedTo && (
            <div className="space-y-1 col-span-2">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Assigned To
              </Label>
              <p className="text-sm">{collectionCase.assignedTo}</p>
            </div>
          )}
        </div>

        {/* Notes - Full Width */}
        {collectionCase.notes && (
          <div className="space-y-1 pt-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Notes
            </Label>
            <p className="text-sm text-muted-foreground">{collectionCase.notes}</p>
          </div>
        )}

        {/* View Source Button */}
        <Link to={`/collections/queue/case/${collectionCase.caseId}`} className="block pt-2">
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            View & Edit Case
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

// Dispute Summary Panel
interface DisputeSummaryPanelProps {
  readonly disputeId: string;
}

export function DisputeSummaryPanel({ disputeId }: DisputeSummaryPanelProps) {
  const { selectedCompany } = useCompany();
  const numericId = parseInt(disputeId, 10) || undefined;
  const { data: dispute = null, isLoading: loading } = useDisputeByNumericId(numericId);

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    const variants: Record<string, string> = {
      'new': 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
      'in progress':
        'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
      resolved:
        'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
      closed: 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
      escalated: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', variants[statusLower] || variants['new'])}>
        {status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityLower = priority.toLowerCase();
    const variants: Record<string, string> = {
      high: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
      medium:
        'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
      low: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', variants[priorityLower] || variants['medium'])}>
        {priority}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dispute) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Dispute Not Found
          </CardTitle>
          <CardDescription>Unable to load dispute details</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          Dispute Summary
        </CardTitle>
        <CardDescription>Key details about this dispute</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Two-column grid layout */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {/* Dispute Type */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Type
            </Label>
            <p className="text-sm font-semibold">{dispute.type}</p>
          </div>

          {/* Amount */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CurrencyIcon currencyCode={dispute.currencyCode || selectedCompany?.currency} className="h-3 w-3" />
              Amount
            </Label>
            <p className="text-sm font-bold">
              {formatCurrency(dispute.amount, dispute.currencyCode || selectedCompany?.currency || 'GBP')}
            </p>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Status
            </Label>
            <div>{getStatusBadge(dispute.status)}</div>
          </div>

          {/* Priority */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Flag className="h-3 w-3" />
              Priority
            </Label>
            <div>{getPriorityBadge(dispute.priority)}</div>
          </div>

          {/* Workflow Level */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              Workflow Level
            </Label>
            <p className="text-sm">
              Level {dispute.currentLevel} of {dispute.totalLevels || getTotalLevelsForDisputeType(dispute.type)}
            </p>
          </div>

          {/* Due Date */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Due Date
            </Label>
            <p className="text-sm">{format(new Date(dispute.dueDate), 'MMM dd, yyyy')}</p>
          </div>

          {/* Assigned To */}
          <div className="space-y-1 col-span-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" />
              Assigned To
            </Label>
            <p className="text-sm">{dispute.assignedTo}</p>
          </div>
        </div>

        {/* Description - Full Width */}
        {dispute.description && (
          <div className="space-y-1 pt-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              Description
            </Label>
            <p className="text-sm text-muted-foreground">{dispute.description}</p>
          </div>
        )}

        {/* AI Recommendation */}
        {dispute.aiRecommendation && (
          <div className="space-y-1 pt-2 bg-blue-50 dark:bg-blue-900/10 p-3 rounded-lg">
            <Label className="text-xs font-medium text-blue-700 dark:text-blue-400 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              AI Recommendation (Score: {dispute.aiScore})
            </Label>
            <p className="text-sm text-blue-900 dark:text-blue-300">{dispute.aiRecommendation}</p>
          </div>
        )}

        {/* View Source Button */}
        <Link to={`/disputes/all/${dispute.id}`} className="block pt-2">
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            View & Edit Dispute
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
